import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Check, Palette } from 'lucide-react';

const ColorPicker = ({
  value = '#3B82F6',
  onChange,
  className = '',
  label = '',
  description = '',
  presetColors = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#F97316', // Orange
    '#EC4899', // Pink
    '#6B7280', // Gray
    '#1F2937', // Dark Gray
    '#000000', // Black
  ],
  showPresets = true,
  showCustom = true,
  disabled = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value);
  const [selectedColor, setSelectedColor] = useState(value);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedColor(value);
    setCustomColor(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    onChange?.(color);
    setIsOpen(false);
  };

  const handleCustomColorChange = (e) => {
    const color = e.target.value;
    setCustomColor(color);
    setSelectedColor(color);
    onChange?.(color);
  };

  const getColorName = (color) => {
    const colorNames = {
      '#3B82F6': 'Blue',
      '#10B981': 'Green',
      '#F59E0B': 'Yellow',
      '#EF4444': 'Red',
      '#8B5CF6': 'Purple',
      '#06B6D4': 'Cyan',
      '#84CC16': 'Lime',
      '#F97316': 'Orange',
      '#EC4899': 'Pink',
      '#6B7280': 'Gray',
      '#1F2937': 'Dark Gray',
      '#000000': 'Black',
    };
    return colorNames[color] || 'Custom';
  };

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-2">
          {label}
          {description && (
            <span className="text-muted-foreground font-normal ml-1">({description})</span>
          )}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 border border-input rounded-md bg-background',
          'hover:border-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring',
          'transition-colors',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        )}
        {...props}
      >
        <div className="flex items-center space-x-3">
          <div
            className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
            style={{ backgroundColor: selectedColor }}
          />
          <span className="text-sm text-foreground">
            {getColorName(selectedColor)}
          </span>
        </div>
        <Palette className="h-4 w-4 text-muted-foreground" />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-md shadow-lg">
          <div className="p-3">
            {showPresets && (
              <div className="mb-4">
                <h4 className="text-xs font-medium text-foreground mb-2">Preset Colors</h4>
                <div className="grid grid-cols-6 gap-2">
                  {presetColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handleColorSelect(color)}
                      className={cn(
                        'w-8 h-8 rounded-full border-2 transition-all hover:scale-110',
                        selectedColor === color
                          ? 'border-foreground shadow-md'
                          : 'border-border hover:border-muted-foreground'
                      )}
                      style={{ backgroundColor: color }}
                      title={getColorName(color)}
                    >
                      {selectedColor === color && (
                        <Check className="h-4 w-4 text-white mx-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showCustom && (
              <div>
                <h4 className="text-xs font-medium text-foreground mb-2">Custom Color</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={customColor}
                    onChange={handleCustomColorChange}
                    className="w-8 h-8 rounded border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground cursor-pointer"
                  />
                  <input
                    type="text"
                    value={customColor}
                    onChange={(e) => {
                      const color = e.target.value;
                      setCustomColor(color);
                      if (/^#[0-9A-F]{6}$/i.test(color)) {
                        setSelectedColor(color);
                        onChange?.(color);
                      }
                    }}
                    placeholder="#000000"
                    className="flex-1 px-2 py-1 text-sm border border-input rounded b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
